import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import {
  Alert,
  Card,
  message,
  PageHeader,
  Button,
  Divider,
  Row,
  Col,
  Form,
} from 'antd';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import MapCard from 'app/containers/Map/parts/MapCard/MapCard';
import {
  MapBoundsForLocationState,
  MapModeEnum,
} from 'app/containers/Map/slice/types';
import { RouteConstants } from 'app/routes';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, RouteComponentProps } from 'react-router-dom';
import { API_URL, LEFT_COL_WIDTH } from 'utils/constants';

import * as S from './AddField.styles';
import { selectSelectedCompanyId } from 'app/containers/Company/slice/selectors';
import { authHeader } from 'utils/helpers';
import { DraggerProps, UploadChangeParam } from 'antd/lib/upload';
import { useFieldSlice } from '../../slice';
import { useMapSlice } from 'app/containers/Map/slice';
import { useTranslation } from 'react-i18next';
import AddFieldsList from '../../parts/AddFieldsList/AddFieldsList';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import ImportFieldsFromSeasonModal from '../../parts/ImportFieldsFromSeasonModal/ImportFieldsFromSeasonModal';
import PermissionsWrapper from 'app/components/PermissionsWrapper/PermissionsWrapper';
import { PermissionsEnum } from 'app/containers/User/slice/types';
import { useResponsiveQuery } from 'utils/responsiveQuery/useResponsiveQuery';
import { useGenerateFieldNames } from '../../hooks/useGenerateFieldNames';

interface IProps extends RouteComponentProps {}

const FIELDS_FORM_ID = 'fieldsForm';

const AddField = ({ history }: IProps) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { mobileOrTablet } = useResponsiveQuery();
  const { actions } = useFieldSlice();
  const { actions: mapActions } = useMapSlice();

  const envId = useSelector(selectSelectedEnvId) as number;
  const seasonId = useSelector(selectSelectedSeasonId);
  const companyId = useSelector(selectSelectedCompanyId);

  const [isDisabledUpload, setIsDisabledUpload] = useState(false);

  const mapRef = useRef<L.Map | null>(null);

  const whenMapCreated = (map: L.Map) => {
    mapRef.current = map;
  };

  useEffect(() => {
    dispatch(actions.clearState());
    dispatch(mapActions.clearUploadFieldsState());
  }, [dispatch, actions, mapActions]);

  useEffect(() => {
    if (envId && seasonId) {
      // Load all polygons on map
      dispatch(
        mapActions.onLoad({ envId, seasonId, companyId, companyGroupId: null }),
      );
    }
  }, [mapActions, dispatch, envId, seasonId, companyId]);

  const props: DraggerProps = useMemo(() => {
    return {
      accept: '.zip,.rar,.7zip',
      name: 'file',
      multiple: false,
      action: `${API_URL}/hiiko_environment/${envId}/fields?company_id=${companyId}`,
      headers: authHeader(),
      onRemove: () => {
        setIsDisabledUpload(false);
      },
      onChange(info: UploadChangeParam) {
        const { status } = info.file;

        if (status === 'done' && envId) {
          const id: string = info.file.response;

          dispatch(actions.getLayerByShapeId({ envId, shapeFileId: id }));
          setIsDisabledUpload(true);

          message.success(
            t('Field.AddField.UploadedMsg', {
              defaultValue: '{{file}} file uploaded successfully.',
              file: info.file.name,
            }),
          );
        } else if (status === 'error') {
          message.error(`${info.file.name} file upload failed.`);
          message.error(
            t('Field.AddField.UploadFailedMsg', {
              defaultValue: '{{file}} file upload failed.',
              file: info.file.name,
            }),
          );
        }
      },
    };
  }, [actions, dispatch, companyId, t, envId]);

  const [form] = Form.useForm();

  const { generateFieldNames } = useGenerateFieldNames(form);

  if (!companyId) {
    return (
      <Alert
        message={t(
          'Field.AddField.SelectCompanyToAddField',
          'To add fields please select a company in header.',
        )}
        type="info"
      />
    );
  }

  const responsivePadding = mobileOrTablet ? 6 : 12;

  const onDrawFieldClick = () => {
    const bounds = mapRef.current?.getBounds();
    if (!bounds) {
      return;
    }

    const mapBounds: MapBoundsForLocationState = {
      northEast: bounds?.getNorthEast(),
      southWest: bounds?.getSouthWest(),
    };

    history.push(
      generatePath(RouteConstants.field.createField, {
        envId,
      }),
      { mapBounds },
    );
  };

  return (
    <>
      <PageHeader
        onBack={() =>
          history.push(
            generatePath(RouteConstants.field.list, {
              envId,
            }),
          )
        }
        className="page-header"
        title={t('Field.AddField.Title', 'Add Fields')}
        extra={[
          <Button key="generate-names" onClick={generateFieldNames}>
            {t(
              'Field.TempFieldsListItem.GenerateFieldNames',
              'Generate field names',
            )}
          </Button>,
          <PermissionsWrapper
            key="submit"
            requiredEditPermissions={[PermissionsEnum.fieldAdministration]}
          >
            <Button
              type="primary"
              form={FIELDS_FORM_ID}
              key="submit"
              htmlType="submit"
            >
              {t('Field.AddField.SaveBtn', 'Save')}
            </Button>
          </PermissionsWrapper>,
        ]}
      />
      <PermissionsWrapper
        show403Card={true}
        requiredEditPermissions={[PermissionsEnum.fieldAdministration]}
      >
        <Row
          wrap={mobileOrTablet}
          gutter={responsivePadding}
          style={{
            paddingLeft: responsivePadding,
            paddingTop: responsivePadding,
            width: '100%',
          }}
        >
          <Col
            style={{
              paddingBottom: 6,
              minWidth: mobileOrTablet ? '100%' : LEFT_COL_WIDTH,
            }}
          >
            <Card bordered={false}>
              <S.UploadDragger {...props} $isDisabled={isDisabledUpload}>
                <>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    {t(
                      'Field.AddField.ImportFieldsWithArchive',
                      'Import fields with a archive file',
                    )}
                  </p>
                  <p className="ant-upload-hint">
                    {t(
                      'Field.AddField.UploadFileOrClickToSelect',
                      'Upload a file or click to select.',
                    )}
                  </p>
                </>
              </S.UploadDragger>
              {!isDisabledUpload && (
                <>
                  <Divider plain>{t('Field.AddField.Or', 'Or')}</Divider>

                  <Button style={{ width: '100%' }} onClick={onDrawFieldClick}>
                    {t('Field.AddField.DrawField', 'Draw field')}
                  </Button>

                  <Button
                    style={{ width: '100%', marginTop: 24 }}
                    onClick={() => {
                      dispatch(actions.toggleImportFieldsFromSeasonModal(true));
                    }}
                  >
                    {t(
                      'Field.AddField.ImportFromAnotherSeason',
                      'Import from another season',
                    )}
                  </Button>
                </>
              )}
            </Card>
            <AddFieldsList formId={FIELDS_FORM_ID} form={form} />
          </Col>
          <S.MapCol flex="auto">
            <MapCard
              mode={MapModeEnum.upload}
              readOnly
              showCulturesMenu={false}
              mapContainerProps={{ whenCreated: whenMapCreated }}
            />
            {isDisabledUpload && (
              <S.MessageCard size="small" bordered={false}>
                <S.CheckIcon />
                {t(
                  'Field.AddField.SelectFields',
                  'Select the fields you want to import.',
                )}
              </S.MessageCard>
            )}
          </S.MapCol>
        </Row>
      </PermissionsWrapper>

      <ImportFieldsFromSeasonModal />
    </>
  );
};

export default AddField;
